/**
 * Partners page content — 100% fidelity to content/company/partners.md
 * Cross-reference: content/company/partners.md
 * 18 sections, all handcrafted from source.
 */

export const partnersMeta = {
  title: "Partners | Perception X2 Partner Ecosystem",
  description:
    "Join the Perception X2 partner ecosystem. Strategic alliances, technology integrations, and channel partnerships for organizations shaping digital perception at scale.",
  slug: "perception-x2-partners",
  canonical: "https://perception.ac/perception-x2-partners",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Partner Ecosystem",
  title: "The Perception X2 Partner Ecosystem.",
  titleAccent: "Built for Scale.",
  lede: "Perception X2 partners with technology providers, strategic advisors, and channel organizations to deliver autonomous perception amplification at global scale.",
  body: "Our partner ecosystem extends the reach of Perception X2 across industries, geographies, and use cases. Through deep integrations, shared technology, and aligned incentives, our partners amplify what the platform delivers alone — 300-500% improvement in narrative penetration and 95-98% organic authenticity across 50+ digital platforms.\n\nPartnering with Perception X2 means access to nine proprietary AI engines, a proven partner framework, and a market position built over 15+ years of operational excellence.",
  cta: [
    { label: "Become a Partner", href: "/contact" },
    { label: "Explore the Ecosystem", href: "#partner-ecosystem" },
  ],
  trustIndicators: [
    "50+ Global Technology and Channel Partners",
    "15+ Years of Partnership Excellence",
    "9 Proprietary AI Engines Available for Integration",
    "Zero Security Incidents Across All Partner Operations",
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  offers: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "Why the Partner Ecosystem Exists",
  intro: "Perception X2 is the world's most advanced autonomous perception amplification platform. But no single organization, regardless of capability, can address every market, every industry, and every use case alone. The Perception X2 partner ecosystem exists to extend our platform's reach, deepen our integrations, and multiply the value we deliver to organizations worldwide.",
  pillars: [
    {
      title: "The Case for Partnership",
      body: "The perception management market is not monolithic. Government agencies require sovereign-level integrations and compliance frameworks. Global enterprises demand seamless CRM and marketing platform connectivity. Healthcare organizations need HIPAA-aligned deployment pathways. Financial institutions require regulatory-grade audit trails. Each vertical has unique requirements that a single platform must adapt to — and partners accelerate that adaptation. Our partner ecosystem solves this through specialization. Technology partners build native integrations that embed Perception X2 capabilities into the tools organizations already use. Channel partners bring localized expertise and market relationships that no centralized team can replicate. Consulting partners provide the strategic advisory layer that transforms platform capability into business outcomes.",
    },
  ],
  offers: [
    { title: "Proprietary Technology Access", body: "Nine AI engines, 50+ platform coverage, 144-dimensional emotional resonance mapping" },
    { title: "Go-to-Market Support", body: "Co-marketing, lead sharing, and joint business development" },
    { title: "Technical Enablement", body: "API access, documentation, sandbox environments, and dedicated engineering support" },
    { title: "Revenue Models", body: "Referral fees, reseller margins, and co-development revenue sharing" },
    { title: "Brand Association", body: "Alignment with 15+ years of operational excellence and 300+ elite global clients" },
  ],
  cta: [
    { label: "Join the Ecosystem", href: "/contact" },
    { label: "Explore Partner Types", href: "#partner-ecosystem" },
  ],
};

export interface ChallengeData {
  eyebrow: string;
  title: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  intro?: string;
}

export const challenge: ChallengeData = {
  eyebrow: "The Challenge",
  title: "Why Organizations Need Partners",
  intro: "",
  pillars: [
    { title: "Market Fragmentation", body: "The average enterprise uses 12-15 different marketing and communications tools. Each tool operates in its own silo with limited cross-platform intelligence. Perception X2 unifies this landscape through nine proprietary AI engines — but integration into existing toolchains requires partner expertise. Without partners, organizations face a choice: rip and replace their entire stack, or operate Perception X2 alongside disconnected tools." },
    { title: "Specialization Requirements", body: "Every industry operates with unique regulatory frameworks, compliance requirements, and operational constraints. Government agencies require FedRAMP-aligned deployment. Healthcare organizations need HIPAA compliance. Financial institutions demand SOC 2 and ISO 27001 certification. These requirements are not optional — they are legal mandates. Partners with deep vertical expertise ensure Perception X2 deployments meet every requirement without compromise." },
    { title: "Geographic and Cultural Complexity", body: "Perception varies by culture, geography, language, and context. A narrative strategy that resonates in one market may fail catastrophically in another. Local partners provide the cultural intelligence, language capability, and market relationships that transform a global platform into a locally effective solution. 100+ language support means nothing without the cultural context to deploy it correctly." },
    { title: "Scale Limitations", body: "Even the most advanced platform has operational limits. A centralized team cannot provide 24/7 support across every timezone, every industry, and every use case. Partners extend operational capacity, providing the localized, responsive support that organizations require. The result is better outcomes, faster response times, and deeper client relationships." },
  ],
  cta: [
    { label: "See How Partnerships Solve This", href: "/about" },
    { label: "Become a Partner", href: "/contact" },
  ],
};

export interface SolutionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const solution: SolutionData = {
  eyebrow: "The Solution",
  title: "The Perception X2 Partner Ecosystem",
  intro: "Perception X2 addresses these challenges through a structured partner ecosystem designed to extend platform capabilities, deepen market reach, and deliver measurable value across every engagement.",
  pillars: [
    { title: "Technology Partnerships", body: "Native integrations that embed Perception X2 capabilities into the tools organizations already use. CRM platforms, marketing automation systems, analytics dashboards, and enterprise communication tools — all connected through our API layer and partner-built connectors. Technology partners ensure Perception X2 operates seamlessly within existing workflows." },
    { title: "Channel Partnerships", body: "Reseller and referral relationships that bring Perception X2 to markets, industries, and geographies we do not directly serve. Channel partners combine our platform capabilities with their market relationships, local expertise, and industry knowledge. The result: faster adoption, better outcomes, and expanded reach." },
    { title: "Strategic Alliances", body: "Deep, co-development relationships with organizations that share our vision for autonomous perception amplification. Strategic alliances produce joint solutions, shared IP, and combined go-to-market efforts that neither party could achieve alone. These are not transactional relationships — they are long-term partnerships built on shared objectives." },
    { title: "Consulting Partnerships", body: "Advisory firms and consulting organizations that recommend, implement, and manage Perception X2 deployments for their clients. Consulting partners provide the strategic layer that transforms platform capability into business outcomes — from initial strategy through execution and optimization." },
  ],
  cta: [
    { label: "Explore Partnership Types", href: "#partner-ecosystem" },
    { label: "Request a Demo", href: "/demo" },
  ],
};

export interface EcosystemData {
  eyebrow: string;
  title: string;
  intro: string;
  rows: { type: string; focus: string; value: string; model: string }[];
  types: { name: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const ecosystem: EcosystemData = {
  eyebrow: "Partner Ecosystem Overview",
  title: "The Four Pillars of Partnership",
  intro: "The Perception X2 partner ecosystem is organized into four distinct partner categories, each designed for specific organizational profiles and market objectives.",
  rows: [
    { type: "Technology Partners", focus: "Platform integrations", value: "Seamless connectivity", model: "API access, co-development" },
    { type: "Channel Partners", focus: "Market expansion", value: "Reach and localization", model: "Reseller, referral, white-label" },
    { type: "Strategic Alliance Partners", focus: "Co-innovation", value: "Joint solutions", model: "Co-development, shared IP" },
    { type: "Consulting Partners", focus: "Advisory and implementation", value: "Strategic guidance", model: "Implementation, managed services" },
  ],
  types: [
    { name: "Technology Partners", body: "Technology partners build native integrations that connect Perception X2 with enterprise toolchains. CRM platforms, marketing automation systems, analytics suites, social media management tools, and enterprise communication platforms. Each integration extends the value of both Perception X2 and the partner's platform." },
    { name: "Channel Partners", body: "Channel partners bring Perception X2 to new markets through reseller agreements, referral programs, and white-label deployment options. They combine our nine AI engines with their market relationships, vertical expertise, and regional presence." },
    { name: "Strategic Alliance Partners", body: "Strategic alliances are deep, co-development relationships with organizations that complement Perception X2 capabilities. Joint solutions, shared intellectual property, and combined go-to-market strategies produce outcomes that exceed what either party could achieve independently." },
    { name: "Consulting Partners", body: "Consulting partners recommend, implement, and manage Perception X2 deployments. They provide the strategic advisory layer — from initial perception audits through long-term optimization — that transforms platform capability into measurable business results." },
  ],
  cta: [
    { label: "Choose Your Partnership Path", href: "#partnership-models" },
    { label: "Become a Partner", href: "/contact" },
  ],
};

export interface TiersData {
  eyebrow: string;
  title: string;
  intro: string;
  rows: { tier: string; requirements: string; benefits: string; model: string }[];
  detail: { name: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const partnerTiers: TiersData = {
  eyebrow: "Partner Tiers",
  title: "Structured for Every Partnership Level",
  intro: "The Perception X2 partner program offers four tiers, each designed for specific partnership depths, commitment levels, and market objectives.",
  rows: [
    { tier: "Registered", requirements: "Application and agreement", benefits: "API access, documentation, basic support", model: "Referral fees" },
    { tier: "Silver", requirements: "$50K+ annual revenue or 5+ active clients", benefits: "Enhanced API limits, co-marketing, priority support", model: "Referral fees + reseller margins" },
    { tier: "Gold", requirements: "$250K+ annual revenue or 25+ active clients", benefits: "Dedicated partner manager, joint GTM, custom integrations", model: "Enhanced margins + co-development" },
    { tier: "Platinum", requirements: "$1M+ annual revenue or strategic alignment", benefits: "Executive sponsorship, shared roadmap, exclusive territory", model: "Full revenue sharing + equity participation" },
  ],
  detail: [
    { name: "Registered Tier", body: "Entry-level partnership providing API access, technical documentation, sandbox environments, and basic support. Registered partners are ideal for organizations evaluating the partnership opportunity or operating at initial scale. Referral fees are earned on all closed deals." },
    { name: "Silver Tier", body: "Performance-based upgrade for partners demonstrating consistent revenue or client acquisition. Silver partners receive enhanced API limits, co-marketing support, priority technical support, and increased referral fees or reseller margins. A dedicated partner portal provides campaign tracking and performance analytics." },
    { name: "Gold Tier", body: "Strategic partnership level for organizations with significant revenue contribution or market impact. Gold partners receive a dedicated partner manager, joint go-to-market planning, custom integration support, and enhanced revenue sharing. Co-development opportunities become available at this tier." },
    { name: "Platinum Tier", body: "The highest partnership level, reserved for organizations with exceptional revenue contribution or strategic alignment. Platinum partners receive executive sponsorship, shared product roadmap input, exclusive territory protection, and full revenue sharing arrangements. These relationships are long-term, high-investment partnerships built for market transformation." },
  ],
  cta: [
    { label: "Apply for Partnership", href: "/contact" },
    { label: "Compare Tiers", href: "#partner-tiers" },
  ],
};

export interface KeyMetricsData {
  eyebrow: string;
  title: string;
  intro: string;
  metrics: { value: string; label: string; detail: string }[];
  pillars?: { title: string; body: string }[];
}

export const keyMetrics: KeyMetricsData = {
  eyebrow: "Key Partnership Metrics",
  title: "The Ecosystem by the Numbers",
  intro: "",
  metrics: [
    { value: "50+", label: "Global Technology and Channel Partners", detail: "Extending Perception X2 reach across industries and geographies" },
    { value: "$10M+", label: "Annual Partner Revenue", detail: "Combined partner-generated revenue across all tiers" },
    { value: "95%", label: "Partner Retention Rate", detail: "Long-term relationships built on shared success" },
    { value: "9", label: "Proprietary AI Engines Available", detail: "Full platform capabilities accessible to all partner tiers" },
    { value: "4", label: "Partner Categories", detail: "Technology, Channel, Strategic Alliance, and Consulting" },
    { value: "100+", label: "Languages Supported Through Partner Network", detail: "Global coverage with local expertise" },
  ],
};

export interface ModelsData {
  eyebrow: string;
  title: string;
  intro: string;
  models: { name: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const partnershipModels: ModelsData = {
  eyebrow: "Partnership Models",
  title: "Flexible Engagement Frameworks",
  intro: "Perception X2 offers multiple partnership models to accommodate different organizational profiles, capabilities, and market objectives.",
  models: [
    { name: "Referral Partnership", body: "The simplest engagement model. Partners refer qualified leads to Perception X2 and earn referral fees on closed deals. No technical integration required — partners leverage their market relationships and industry knowledge to identify opportunities. Referral fees scale with deal size and partner tier." },
    { name: "Reseller Partnership", body: "Partners sell Perception X2 directly to their clients, combining our platform capabilities with their market presence, vertical expertise, and client relationships. Reseller agreements include discounted pricing, co-branded materials, and dedicated support. Resellers manage client relationships while Perception X2 handles platform delivery." },
    { name: "Technology Integration Partnership", body: "Partners build native integrations connecting Perception X2 with enterprise platforms — CRM systems, marketing automation tools, analytics suites, and communication platforms. These integrations extend the value of both platforms and create stickier client relationships. Integration partners receive API access, technical support, and co-marketing opportunities." },
    { name: "Co-Development Partnership", body: "The deepest engagement model. Partners and Perception X2 jointly develop new capabilities, solutions, or market offerings. Co-development partnerships involve shared engineering resources, joint intellectual property, and combined go-to-market strategies. These relationships produce innovations that neither party could achieve alone." },
    { name: "White-Label Partnership", body: "Partners deploy Perception X2 capabilities under their own brand. White-label agreements include full platform access, custom branding, and dedicated support. Partners maintain client relationships while leveraging Perception X2 technology as the underlying engine." },
  ],
  cta: [
    { label: "Choose Your Model", href: "#partnership-models" },
    { label: "Start a Conversation", href: "/contact" },
  ],
};

export interface TechPartnersData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const technologyPartners: TechPartnersData = {
  eyebrow: "Technology Partners",
  title: "Platform Integrations That Matter",
  intro: "Technology partners build the integrations that embed Perception X2 capabilities into enterprise workflows. Every integration is designed to extend the value of both platforms while maintaining security, performance, and organic authenticity standards.",
  pillars: [
    { title: "CRM & Sales Platforms", body: "Native integrations with leading CRM systems ensure perception intelligence flows directly into sales and account management workflows. Real-time sentiment data, competitive perception metrics, and narrative performance indicators are accessible within the tools client teams already use. No context switching. No data silos." },
    { title: "Marketing Automation", body: "Integration with marketing automation platforms enables seamless coordination between perception amplification campaigns and broader marketing efforts. Content libraries, deployment schedules, and performance metrics sync automatically — ensuring every channel operates from a single source of truth." },
    { title: "Analytics & Business Intelligence", body: "Connect Perception X2 analytics to enterprise BI platforms for consolidated reporting and executive visibility. 144-dimensional emotional resonance data, narrative penetration metrics, and organic authenticity scores are available alongside existing KPIs. One dashboard. Complete visibility." },
    { title: "Social Media Management", body: "Integration with social media management platforms ensures Perception X2 amplification campaigns coordinate with organic social efforts. Platform-specific optimization, scheduling alignment, and engagement tracking are unified across managed and amplified content." },
    { title: "Enterprise Communication", body: "Connect Perception X2 to enterprise communication platforms for real-time alerts, crisis notifications, and stakeholder updates. When perception shifts require action, the right people are notified instantly through the channels they already use." },
  ],
  cta: [
    { label: "Explore Integrations", href: "/partners" },
    { label: "Become a Technology Partner", href: "/contact" },
  ],
};

export interface StrategicAlliancesData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const strategicAlliances: StrategicAlliancesData = {
  eyebrow: "Strategic Alliances",
  title: "Co-Innovation at Scale",
  intro: "Strategic alliances represent the deepest form of partnership within the Perception X2 ecosystem. These are long-term, high-investment relationships built on shared vision, complementary capabilities, and mutual commitment to market transformation.",
  pillars: [
    { title: "Joint Solution Development", body: "Strategic alliance partners co-develop solutions that combine Perception X2 AI capabilities with partner expertise. Joint solutions address specific market needs that neither party could address independently — from industry-specific perception management frameworks to integrated intelligence platforms." },
    { title: "Shared Intellectual Property", body: "Co-development produces shared IP that creates competitive moats for both partners. Patents, proprietary methodologies, and joint platform capabilities are developed, owned, and licensed through partnership agreements. Shared IP deepens the relationship and creates long-term value." },
    { title: "Combined Go-to-Market", body: "Strategic alliances include combined go-to-market strategies — joint marketing campaigns, co-branded content, shared events, and coordinated sales efforts. These strategies amplify the reach of both partners while maintaining brand integrity and market positioning." },
    { title: "Executive Alignment", body: "Strategic alliances operate with executive sponsorship from both organizations. Regular executive reviews ensure alignment on objectives, investment, and outcomes. These are not operational relationships — they are strategic commitments at the highest level." },
    { title: "Long-Term Roadmap Sharing", body: "Strategic partners receive early access to product roadmaps, beta features, and development priorities. This transparency enables partners to plan their own development around Perception X2 evolution, ensuring alignment and reducing integration friction." },
  ],
  cta: [
    { label: "Explore Strategic Partnerships", href: "/partners" },
    { label: "Begin the Conversation", href: "/contact" },
  ],
};

export interface ChannelPartnersData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const channelPartners: ChannelPartnersData = {
  eyebrow: "Channel Partners",
  title: "Extending Reach Through Local Expertise",
  intro: "Channel partners bring Perception X2 to markets, industries, and geographies through reseller agreements, referral programs, and white-label deployment options. They are the bridge between global platform capability and local market effectiveness.",
  pillars: [
    { title: "Reseller Partners", body: "Reseller partners sell Perception X2 directly to their client base. They combine our nine AI engines with their market relationships, vertical expertise, and regional presence. Reseller agreements include discounted pricing, co-branded marketing materials, dedicated support, and performance-based incentive programs." },
    { title: "Referral Partners", body: "Referral partners identify and introduce qualified opportunities to Perception X2. They leverage their industry knowledge and market relationships to connect us with organizations that need autonomous perception amplification. Referral fees are earned on closed deals, with fees scaling based on partner tier and deal size." },
    { title: "White-Label Partners", body: "White-label partners deploy Perception X2 capabilities under their own brand. Full platform access, custom branding, dedicated support, and flexible pricing models enable partners to offer perception amplification as part of their existing service portfolio. Clients receive Perception X2 capabilities through a trusted, familiar provider." },
    { title: "Regional Specialists", body: "Regional channel partners provide localized expertise — cultural intelligence, language capability, regulatory knowledge, and market relationships — that transforms a global platform into a locally effective solution. Regional specialists ensure Perception X2 deployments respect cultural nuances and meet local requirements." },
  ],
  cta: [
    { label: "Join the Channel", href: "/partners" },
    { label: "Become a Partner", href: "/contact" },
  ],
};

export interface ConsultingPartnersData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const consultingPartners: ConsultingPartnersData = {
  eyebrow: "Consulting Partners",
  title: "The Advisory Layer",
  intro: "Consulting partners recommend, implement, and manage Perception X2 deployments for their clients. They provide the strategic advisory layer that transforms platform capability into business outcomes — from initial perception audits through long-term optimization and governance.",
  pillars: [
    { title: "Implementation Partners", body: "Implementation partners design and execute Perception X2 deployments. They assess client needs, configure platform settings, develop narrative strategies, and manage initial campaigns. Implementation partners ensure deployments are aligned with business objectives from day one." },
    { title: "Managed Service Partners", body: "Managed service partners operate Perception X2 on behalf of their clients. They provide ongoing campaign management, performance monitoring, optimization, and reporting. Managed services are ideal for organizations that want perception amplification outcomes without building internal capabilities." },
    { title: "Advisory Partners", body: "Advisory partners recommend Perception X2 as part of broader strategic consulting engagements. They assess client perception challenges, evaluate platform fit, and facilitate the connection between client needs and Perception X2 capabilities. Advisory partners earn referral fees on resulting engagements." },
    { title: "Training Partners", body: "Training partners deliver Perception X2 certification programs and user training. They ensure client teams are equipped to leverage platform capabilities effectively. Training partnerships include curriculum development, instructor certification, and ongoing content updates." },
  ],
  cta: [
    { label: "Find a Consulting Partner", href: "/partners" },
    { label: "Become a Consulting Partner", href: "/contact" },
  ],
};

export interface BenefitsData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const partnerBenefits: BenefitsData = {
  eyebrow: "Partner Benefits",
  title: "What Partnership Delivers",
  intro: "Every Perception X2 partner receives benefits designed to accelerate market success, deepen platform expertise, and maximize revenue opportunity.",
  pillars: [
    { title: "Technology Access", body: "Full access to nine proprietary AI engines, RESTful API integration, sandbox environments, and technical documentation. Partners leverage the same technology that powers 300-500% improvement in narrative penetration for 300+ elite global clients." },
    { title: "Revenue Opportunity", body: "Competitive referral fees, reseller margins, and co-development revenue sharing. Revenue models scale with partner tier, performance, and engagement depth. Partners earn while building long-term, sustainable businesses around Perception X2 capabilities." },
    { title: "Go-to-Market Support", body: "Co-branded marketing materials, joint campaign support, lead sharing, and event co-sponsorship. Perception X2 invests in partner success through marketing resources that accelerate pipeline development and close rates." },
    { title: "Technical Enablement", body: "Dedicated partner portal, technical documentation, API reference, sandbox environments, and priority technical support. Partners receive the resources they need to build, integrate, and deploy effectively." },
    { title: "Training & Certification", body: "Comprehensive training programs covering platform capabilities, integration development, deployment best practices, and sales methodologies. Certification programs validate expertise and differentiate partners in the market." },
    { title: "Dedicated Support", body: "Priority access to technical support, partner success managers, and engineering resources. Partners receive the responsive, expert support that keeps deployments running and clients satisfied." },
  ],
  cta: [
    { label: "Review Partner Benefits", href: "/partners" },
    { label: "Start Your Partnership", href: "/contact" },
  ],
};

export interface EnablementData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const partnerEnablement: EnablementData = {
  eyebrow: "Partner Enablement",
  title: "Tools, Resources, and Support",
  intro: "Perception X2 provides comprehensive enablement resources designed to accelerate partner success from initial onboarding through ongoing growth.",
  pillars: [
    { title: "Partner Portal", body: "A centralized digital platform providing access to technical documentation, API references, marketing materials, sales tools, training content, and performance analytics. The partner portal is the hub for all partner operations — from lead registration to deal tracking to revenue reporting." },
    { title: "Sandbox Environment", body: "Full-featured sandbox environments that replicate Perception X2 production capabilities. Partners test integrations, validate deployments, and develop solutions without impacting live client environments. Sandbox access includes all nine AI engines and full API functionality." },
    { title: "Technical Documentation", body: "Comprehensive documentation covering API integration, platform configuration, deployment best practices, and optimization strategies. Documentation is continuously updated to reflect new features, capabilities, and partnership models." },
    { title: "Training Programs", body: "Structured training programs covering platform capabilities, integration development, sales methodologies, and client management. Training is delivered through on-demand courses, live workshops, and hands-on lab sessions. Certification validates expertise and differentiates partners." },
    { title: "Co-Marketing Resources", body: "Co-branded marketing materials, campaign templates, content libraries, and event support. Perception X2 invests in partner marketing through shared campaigns, joint content development, and coordinated event presence." },
    { title: "Partner Success Management", body: "Dedicated partner success managers provide ongoing support, performance reviews, and growth planning. Regular business reviews ensure alignment on objectives, identify expansion opportunities, and optimize partnership value." },
  ],
  cta: [
    { label: "Access Partner Resources", href: "/partners" },
    { label: "Begin Onboarding", href: "/contact" },
  ],
};

export interface SuccessData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const partnerSuccess: SuccessData = {
  eyebrow: "Partner Success Criteria",
  title: "Measurable Outcomes for Every Partnership",
  intro: "The Perception X2 partner ecosystem is built on measurable success. Every partnership is evaluated against clear criteria that ensure mutual value and sustainable growth.",
  pillars: [
    { title: "Revenue Performance", body: "Partners are measured on revenue contribution, deal velocity, and pipeline growth. Revenue targets are established during partnership onboarding and reviewed quarterly. Performance against targets determines tier status, incentive levels, and expansion opportunities." },
    { title: "Client Outcomes", body: "Partner success is ultimately defined by client success. Perception X2 tracks client satisfaction, platform adoption, narrative penetration improvement, and organic authenticity ratings across partner-managed deployments. Partners who consistently deliver strong client outcomes receive enhanced support and recognition." },
    { title: "Technical Quality", body: "Integration partners are evaluated on integration quality, reliability, and client feedback. Technical standards ensure that partner-built integrations maintain the security, performance, and authenticity standards that Perception X2 delivers directly." },
    { title: "Market Development", body: "Partners are recognized for market development contributions — new client acquisition, industry expansion, geographic coverage, and thought leadership. Market development activities strengthen the ecosystem and create opportunities for all partners." },
    { title: "Ecosystem Contribution", body: "The most successful partnerships go beyond revenue. Partners who contribute to ecosystem growth — through training, content development, integration innovation, and community building — receive recognition and expanded partnership privileges." },
  ],
  cta: [
    { label: "Define Your Success Metrics", href: "/partners" },
    { label: "Discuss Partnership Goals", href: "/contact" },
  ],
};

export interface WhyPartnerData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const whyPartner: WhyPartnerData = {
  eyebrow: "Why Partner With Us",
  title: "The Perception X2 Partnership Advantage",
  intro: "",
  pillars: [
    { title: "Proven Technology", body: "Nine proprietary AI engines refined through 15+ years of operational excellence. Not licensed, not white-labeled — built, tested, and proven in the world's most demanding environments. Partners leverage technology that cannot be replicated." },
    { title: "Market Position", body: "300+ elite global clients. Operations across multiple continents. 99.9999% platform uptime. Perception X2 is the established leader in autonomous perception amplification. Partners align with the brand that the market trusts." },
    { title: "Revenue Opportunity", body: "Competitive margins, scalable incentives, and long-term revenue models. The perception management market is growing rapidly — partners who establish themselves now capture value for years to come." },
    { title: "Ecosystem Depth", body: "Technology integrations, channel reach, strategic alliances, and consulting partnerships. The Perception X2 ecosystem provides multiple pathways to market success, regardless of organizational profile or capability focus." },
    { title: "Innovation Pipeline", body: "Continuous platform advancement ensures partners always have new capabilities to offer. Regular feature releases, engine improvements, and platform expansions keep the partnership fresh and the market opportunity growing." },
    { title: "Shared Success", body: "Perception X2 partner success is our success. We invest in partner growth through enablement resources, go-to-market support, and dedicated partnership management. Your growth strengthens the ecosystem. Our ecosystem accelerates your growth." },
  ],
  cta: [
    { label: "Choose Partnership", href: "/partners" },
    { label: "Start the Conversation", href: "/contact" },
  ],
};

export interface CtaData {
  eyebrow: string;
  title: string;
  intro: string;
  body: string;
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const partnerCta: CtaData = {
  eyebrow: "Get Started",
  title: "Ready to Join the Perception X2 Partner Ecosystem?",
  intro: "The perception management market is growing at unprecedented rates. Organizations across every industry and geography are recognizing that perception is not a byproduct of performance — it is a strategic asset that requires active management. The partners who position themselves now will capture disproportionate value as this market expands.",
  body: "Perception X2 offers the technology, the brand, and the partnership framework to make that positioning possible. Nine proprietary AI engines. 50+ platform coverage. 300-500% improvement in narrative penetration. 95-98% organic authenticity. These are the capabilities your clients need — and the partnership will deliver.",
  cta: [
    { label: "Apply for Partnership", href: "/contact" },
    { label: "Already a Partner? Access the Partner Portal", href: "/contact" },
  ],
};

export interface ContactInfoData {
  eyebrow: string;
  title: string;
  intro: string;
  contacts: { label: string; email: string; note?: string }[];
  hours: { label: string; body: string }[];
  pillars?: { title: string; body: string }[];
}

export const contactInfo: ContactInfoData = {
  eyebrow: "Contact Information",
  title: "Start the Conversation",
  intro: "",
  contacts: [
    { label: "Partnership Inquiries", email: "partners@perception.ac", note: "Response time: Within 48 hours" },
    { label: "Technology Partnership Requests", email: "tech-partners@perception.ac", note: "Include: Platform details, integration scope, and organizational profile" },
    { label: "Channel Partnership Applications", email: "channel@perception.ac", note: "Include: Market focus, client base, and geographic coverage" },
    { label: "Strategic Alliance Discussions", email: "alliances@perception.ac", note: "Include: Strategic objectives, capability overview, and partnership vision" },
    { label: "General Inquiries", email: "contact@perception.ac" },
  ],
  hours: [
    { label: "Support Hours", body: "24/7/365 support for active partners" },
    { label: "Inquiry Hours", body: "Monday-Friday, 9:00 AM - 6:00 PM (local time) for partnership inquiries" },
  ],
};
